<div
  class="flex flex-col gap-2xl"
  [formGroup]="delegationForm"
  libScrollToError>
  @if (isLoading()) {
    <lib-overlay-loader />
  } @else {
    <header class="flex items-center gap-md pb-lg border-b border-neutral-200">
      <button type="button" id="delegation_form_back_button" (click)="goBack()">
        <lib-chevron-down-icon
          [iconClass]="'size-10 text-neutral-900 rotate-90 p-sm rounded-lg cursor-pointer border border-neutral-200'" />
      </button>
      <h1 class="text-xl font-semibold text-neutral-900">
        {{ pageTitle() }}
      </h1>
    </header>

    <lib-expand-collapse-all
      [(isAllSectionsExpanded)]="isAllSectionsExpanded" />

    <lib-section-header
      [title]="'delegation_form_screen_details_section_title' | transloco"
      [isAccordion]="true"
      [isAccordionOpen]="isAllSectionsExpanded()"
      [contentTemplate]="detailsSection">
      <ng-template #detailsSection>
        <div
          class="grid [grid-template-columns:repeat(auto-fill,minmax(20rem,1fr))] gap-lg mt-xl">
          <div>
            <lib-lookup-field
              [id]="'delegate_id'"
              [inputFormControlName]="'delegate_id'"
              [name]="'delegate_id'"
              [label]="'delegation_form_screen_delegate_to_label' | transloco"
              [placeholder]="
                'delegation_form_screen_delegate_to_placeholder' | transloco
              "
              [required]="true"
              [apiUrl]="userLookupApi"
              [filters]="lookupSearchFilters()"
              [selectedOption]="selectedDelegatee()"
              [labelValueAssignList]="userLookupAssignList()"
              [searchPlaceholder]="'type_to_search_placeholder' | transloco"
              [noSearchResultLabel]="
                'user_dropdown_input_no_data_label' | transloco
              "
              [detailsNotFoundLabel]="'details_not_found_label' | transloco" />
            <lib-validation-message
              [lang]="'current_lang' | transloco"
              [control]="'delegate_id'" />
          </div>

          <div>
            <lib-date-picker
              [id]="'started_at'"
              [inputFormControlName]="'started_at'"
              [name]="'started_at'"
              [label]="'delegation_form_screen_period_from_label' | transloco"
              [placeholder]="
                'delegation_form_screen_period_from_label' | transloco
              "
              [required]="true"
              [showTime]="false"
              [disabled]="isStartedAtDisabled()"
              [minDate]="periodFromMinDate()"
              [lang]="'current_lang' | transloco"
              [closeButtonText]="'date_picker_close_button' | transloco"
              [applyButtonText]="'date_picker_apply_button' | transloco" />
            <lib-validation-message
              [lang]="'current_lang' | transloco"
              [control]="'started_at'" />
          </div>

          <div>
            <lib-date-picker
              [id]="'ended_at'"
              [inputFormControlName]="'ended_at'"
              [name]="'ended_at'"
              [label]="'delegation_form_screen_period_to_label' | transloco"
              [placeholder]="
                'delegation_form_screen_period_to_label' | transloco
              "
              [required]="true"
              [showTime]="false"
              [minDate]="periodToMinDate()"
              [lang]="'current_lang' | transloco"
              [closeButtonText]="'date_picker_close_button' | transloco"
              [applyButtonText]="'date_picker_apply_button' | transloco" />
            <lib-validation-message
              [lang]="'current_lang' | transloco"
              [control]="'ended_at'" />
          </div>

          <div class="col-[1/3]">
            <div [formGroupName]="'reason'">
              <lib-rich-editor-dual-lang-text-area
                [inputFormControlName]="'reason'"
                [id]="'reason'"
                [name]="'reason'"
                [label]="'delegation_form_screen_reason_label' | transloco"
                [requiredGroup]="true"
                [requiredEn]="false"
                [requiredAr]="false"
                [width]="'100%'"
                [canResize]="false"
                [placeholderEn]="
                  'delegation_form_screen_reason_placeholder_en' | transloco
                "
                [placeholderAr]="
                  'delegation_form_screen_reason_placeholder_ar' | transloco
                " />
            </div>
            <lib-validation-message
              [lang]="'current_lang' | transloco"
              [control]="'reason'" />
          </div>
        </div>
      </ng-template>
    </lib-section-header>

    <lib-section-header
      [title]="'delegation_form_screen_workflows_section_title' | transloco"
      [isAccordion]="true"
      [isAccordionOpen]="isAllSectionsExpanded()"
      [contentTemplate]="workflowsSection">
      <ng-template #workflowsSection>
        <div class="mt-xl">
          <lib-multi-lookup-field
            [id]="'flow_template_ids'"
            [inputFormControlName]="'flow_template_ids'"
            [name]="'flow_template_ids'"
            [label]="
              'delegation_form_screen_select_workflows_label' | transloco
            "
            [placeholder]="
              'delegation_form_screen_select_workflows_placeholder' | transloco
            "
            [required]="true"
            [apiUrl]="workflowLookupApi()"
            [filters]="workflowLookupSearchFilters()"
            [searchKeywordKey]="'keyword'"
            [selectedKeyValue]="'id'"
            [selectedKeyName]="'name'"
            [size]="{
              min_size: 1,
              max_size: 100,
            }"
            [selectedOption]="selectedWorkflows()"
            [labelValueAssignList]="workflowLookupAssignList()"
            [lang]="'current_lang' | transloco"
            [searchPlaceholder]="'type_to_search_placeholder' | transloco"
            [detailsNotFoundLabel]="'details_not_found_label' | transloco"
            [addNewAssignLabel]="
              'delegation_form_screen_add_new_workflow_button' | transloco
            "
            [selectAllLabel]="
              'multi_lookup_field_select_all_label' | transloco
            "
            [enableSelectAll]="true"
            [noSearchResultLabel]="
              'user_dropdown_input_no_data_label' | transloco
            " />
        </div>
      </ng-template>
    </lib-section-header>

    <lib-section-header
      [title]="'delegation_form_screen_tasks_section_title' | transloco"
      [isAccordion]="true"
      [isAccordionOpen]="isAllSectionsExpanded()"
      [contentTemplate]="tasksSection">
      <ng-template #tasksSection>
        <div class="mt-xl">
          <lib-radio-button
            [formGroup]="delegationForm"
            [inputFormControlName]="'task_scope'"
            id="task_scope"
            [required]="true"
            [class]="'flex gap-4xl'"
            [options]="taskScopeOptions()" />
          <lib-validation-message
            [lang]="'current_lang' | transloco"
            [control]="'task_scope'" />
        </div>
      </ng-template>
    </lib-section-header>

    <div
      class="flex py-2xl gap-lg border-t border-neutral-200 justify-end items-end">
      <button
        type="button"
        id="delegation_form_cancel_button"
        class="px-2xl py-sm rounded-md text-neutral-700 bg-white text-lg font-medium border border-neutral-300 cursor-pointer"
        (click)="goBack()">
        {{ 'delegation_form_screen_cancel_button' | transloco }}
      </button>

      <button
        type="button"
        id="delegation_form_submit_button"
        class="px-2xl py-sm rounded-md text-white bg-primary-500 text-lg font-medium border-none cursor-pointer"
        [class.opacity-30]="isSubmitting()"
        [disabled]="isSubmitting()"
        (click)="submit()">
        {{ submitButtonLabel() }}
      </button>
    </div>
  }
</div>
